<template>
  <el-dialog
    title="选择国家和运营商"
    :visible.sync="dialogVisible"
    width="950px"
    :before-close="handleDialogClose"
    destroy-on-close
    custom-class="country-operator-dialog"
  >
    <!-- 顶部操作区：全选复选框 + 搜索框 -->
    <div class="header-operation">
      <el-checkbox
        v-model="allSelected"
        :indeterminate="isIndeterminate"
        class="all-select-checkbox"
      >
        全选
      </el-checkbox>
      <el-input
        v-model="searchKeyword"
        placeholder="请输入国家名称、国家码、运营商名称搜索"
        clearable
        prefix-icon="el-icon-search"
        class="search-input"
      />
    </div>

    <!-- 核心内容区：国家列表 + 字母导航 -->
    <div class="content-wrapper">
      <!-- 国家列表滚动容器 -->
      <div class="country-list-wrapper" ref="countryListWrapper">
        <!-- 循环国家列表 -->
        <div 
          v-for="country in filterCountryList" 
          :key="country.countryId" 
          :id="'country-' + country.countryId"
          class="country-item"
        >
          <!-- 国家复选框 + 名称行（修复垂直居中） -->
          <div class="country-header">
            <el-checkbox
              v-model="selectedCountryMap[country.countryId].checked"
              :indeterminate="selectedCountryMap[country.countryId].indeterminate"
              @change.native.stop="handleCountryChange(country)"
              class="country-checkbox"
            >
              <span class="country-name">{{ country.countryName }}</span>
              <span class="country-code" v-if="country.countryCode">({{ country.countryCode }})</span>
            </el-checkbox>
          </div>

          <!-- 运营商列表：直接展示 -->
          <div class="operator-list">
            <el-checkbox
              v-for="operator in country.operatorList"
              :key="operator.operatorId"
              v-model="selectedCountryMap[country.countryId].operatorMap[operator.operatorId]"
              @change="handleOperatorChange(country)"
              class="operator-checkbox"
            >
              {{ operator.operatorName }}
            </el-checkbox>
          </div>
        </div>
        <!-- 空数据提示 -->
        <div class="empty-tip" v-if="filterCountryList.length === 0">
          暂无匹配的国家数据
        </div>
      </div>

      <!-- A-Z 首字母导航 -->
      <div class="letter-nav">
        <div
          v-for="letter in letterList"
          :key="letter"
          class="letter-item"
          :class="{ 'disabled': !letterCountryMap[letter], 'active': activeLetter === letter }"
          @click="scrollToLetter(letter)"
        >
          {{ letter }}
        </div>
      </div>
    </div>

    <!-- 底部选中预览 -->
    <div class="selected-preview" v-if="selectedTotal > 0">
      已选中 <span class="selected-count">{{ selectedTotal }}</span> 个国家，{{ selectedOperatorTotal }} 个运营商
    </div>

    <!-- 底部按钮 -->
    <div slot="footer" class="dialog-footer">
      <el-button @click="dialogVisible = false">取消</el-button>
      <el-button type="primary" @click="confirmSelection">确定</el-button>
    </div>
  </el-dialog>
</template>

<script>
// 可选：用于生成国家名称首字母，若后端已返回可删除
import { pinyin } from 'pinyin-pro'
export default {
  name: "CountryOperatorSelector",
  props: {
    countryList: {
      type: Array,
      default: function () {
        return []
      }
    }
  },
  data() {
    return {
      // 弹框显隐
      dialogVisible: false,
      // 搜索关键词
      searchKeyword: '',
      // 防抖定时器
      searchTimer: null,
      // 当前高亮字母
      activeLetter: '',
      // 内部全量国家数据源
      fullCountryList: [
        {
          countryId: 1,
          countryName: "中国",
          countryCode: "CN",
          firstLetter: "Z",
          isHot: true,
          isExpand: true,
          operatorList: [
            { operatorId: 101, operatorName: "中国移动", operatorCode: "CMCC" },
            { operatorId: 102, operatorName: "中国联通", operatorCode: "CUCC" },
            { operatorId: 103, operatorName: "中国电信", operatorCode: "CTCC" },
          ],
        },
        {
          countryId: 6,
          countryName: "中国澳门",
          countryCode: "CN",
          firstLetter: "Z",
          isHot: true,
          isExpand: true,
          operatorList: [
            { operatorId: 101, operatorName: "中国移动", operatorCode: "CMCC" },
            { operatorId: 102, operatorName: "中国联通", operatorCode: "CUCC" },
            { operatorId: 103, operatorName: "中国电信", operatorCode: "CTCC" },
          ],
        },
        {
          countryId: 2,
          countryName: "美国",
          countryCode: "US",
          firstLetter: "M",
          isHot: true,
          isExpand: true,
          operatorList: [
            { operatorId: 201, operatorName: "Telefonica", operatorCode: "TEF" },
            { operatorId: 202, operatorName: "Vivo S.A", operatorCode: "VIVO" },
          ],
        },
        {
          countryId: 3,
          countryName: "日本",
          countryCode: "US",
          firstLetter: "R",
          isHot: true,
          isExpand: true,
          operatorList: [
            { operatorId: 201, operatorName: "Telefonica", operatorCode: "TEF" },
            { operatorId: 202, operatorName: "Vivo S.A", operatorCode: "VIVO" },
          ],
        },
        {
          countryId: 4,
          countryName: "韩国",
          countryCode: "US",
          firstLetter: "H",
          isHot: true,
          isExpand: true,
          operatorList: [
            { operatorId: 201, operatorName: "Telefonica", operatorCode: "TEF" },
            { operatorId: 202, operatorName: "Vivo S.A", operatorCode: "VIVO" },
          ],
        },
        {
          countryId: 5,
          countryName: "德国",
          countryCode: "US",
          firstLetter: "D",
          isHot: true,
          isExpand: true,
          operatorList: [
            { operatorId: 201, operatorName: "Telefonica", operatorCode: "TEF" },
            { operatorId: 202, operatorName: "Vivo S.A", operatorCode: "VIVO" },
          ],
        },
        {
          countryId: 7,
          countryName: "德国",
          countryCode: "US",
          firstLetter: "D",
          isHot: true,
          isExpand: true,
          operatorList: [
            { operatorId: 201, operatorName: "Telefonica", operatorCode: "TEF" },
            { operatorId: 202, operatorName: "Vivo S.A", operatorCode: "VIVO" },
          ],
        },
        {
          countryId: 8,
          countryName: "德国",
          countryCode: "US",
          firstLetter: "D",
          isHot: true,
          isExpand: true,
          operatorList: [
            { operatorId: 201, operatorName: "Telefonica", operatorCode: "TEF" },
            { operatorId: 202, operatorName: "Vivo S.A", operatorCode: "VIVO" },
          ],
        },
        {
          countryId: 101,
          countryName: "中国123",
          countryCode: "CN",
          firstLetter: "Z",
          isHot: true,
          isExpand: true,
          operatorList: [
            { operatorId: 101, operatorName: "中国移动", operatorCode: "CMCC" },
            { operatorId: 102, operatorName: "中国联通", operatorCode: "CUCC" },
            { operatorId: 103, operatorName: "中国电信", operatorCode: "CTCC" },
          ],
        },
      ],
      // 选中状态存储
      selectedCountryMap: {},
      // 字母-国家映射表
      letterCountryMap: {},
      // A-Z字母列表
      letterList: Array.from({ length: 26 }, function (_, i) {
        return String.fromCharCode(65 + i)
      }),
    };
  },
  computed: {
    // 全选复选框状态
    allSelected: {
      get: function () {
        var list = this.filterCountryList
        if (list.length === 0) return false
        return list.every(function (country) {
          var state = this.selectedCountryMap[country.countryId]
          return state && state.checked
        }.bind(this))
      },
      set: function (val) {
        var list = this.filterCountryList
        list.forEach(function (country) {
          var countryState = this.selectedCountryMap[country.countryId]
          if (!countryState) return
          this.$set(countryState, 'checked', val)
          this.handleCountryChange(country)
        }.bind(this))
      }
    },
    // 全选框半选状态
    isIndeterminate: function () {
      var list = this.filterCountryList
      if (list.length === 0) return false
      var hasChecked = false
      var hasUnchecked = false
      for (var i = 0; i < list.length; i++) {
        var country = list[i]
        var state = this.selectedCountryMap[country.countryId]
        if (!state) continue
        if (state.checked || state.indeterminate) {
          hasChecked = true
        } else {
          hasUnchecked = true
        }
      }
      return hasChecked && hasUnchecked
    },
    // 过滤后的国家列表
    filterCountryList: function () {
      var list = JSON.parse(JSON.stringify(this.fullCountryList))
      var keyword = this.searchKeyword.trim().toLowerCase()

      if (keyword) {
        list = list.filter(function (country) {
          var isCountryMatch = 
            country.countryName.toLowerCase().indexOf(keyword) > -1 ||
            (country.countryCode && country.countryCode.toLowerCase().indexOf(keyword) > -1)
          
          var isOperatorMatch = country.operatorList.some(function (op) {
            return op.operatorName.toLowerCase().indexOf(keyword) > -1
          })

          return isCountryMatch || isOperatorMatch
        })
      }

      return list.sort(function (a, b) {
        if (a.isHot && !b.isHot) return -1
        if (!a.isHot && b.isHot) return 1
        return 0
      })
    },
    // 已选中国家总数
    selectedTotal: function () {
      var count = 0
      var countryList = Object.values(this.selectedCountryMap)
      for (var i = 0; i < countryList.length; i++) {
        var item = countryList[i]
        if (item && (item.checked || item.indeterminate)) {
          count++
        }
      }
      return count
    },
    // 已选中运营商总数
    selectedOperatorTotal: function () {
      var total = 0
      var countryList = Object.values(this.selectedCountryMap)
      for (var i = 0; i < countryList.length; i++) {
        var country = countryList[i]
        if (!country || !country.operatorMap) continue
        var operatorCheckedList = Object.values(country.operatorMap)
        for (var j = 0; j < operatorCheckedList.length; j++) {
          if (operatorCheckedList[j]) {
            total++
          }
        }
      }
      return total
    }
  },
  watch: {
    countryList: {
      handler: function (newVal) {
        if (newVal && newVal.length > 0) {
          for (var i = 0; i < newVal.length; i++) {
            newVal[i].isExpand = true
          }
          this.fullCountryList = newVal
        }
      },
      immediate: true,
      deep: true
    },
    fullCountryList: {
      handler: function () {
        this.initSelectedMap()
      },
      immediate: true,
      deep: true
    },
    searchKeyword: function (val) {
      var _this = this
      if (this.searchTimer) clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(function () {
        _this.$nextTick(function () {
          if (_this.$refs.countryListWrapper) {
            _this.$refs.countryListWrapper.scrollTop = 0
          }
        })
      }, 300)
    },
    dialogVisible: function (val) {
      var _this = this
      if (val) {
        this.initLetterMap()
        this.$nextTick(function () {
          if (_this.$refs.countryListWrapper) {
            _this.$refs.countryListWrapper.addEventListener('scroll', _this.handleScroll)
            _this.$refs.countryListWrapper.scrollTop = 0
          }
        })
      } else {
        if (this.$refs.countryListWrapper) {
          this.$refs.countryListWrapper.removeEventListener('scroll', this.handleScroll)
        }
      }
    }
  },
  methods: {
    initSelectedMap: function () {
      var _this = this
      var newSelectedMap = {}
      for (var i = 0; i < this.fullCountryList.length; i++) {
        var country = this.fullCountryList[i]
        country.isExpand = true
        var operatorMap = {}
        for (var j = 0; j < country.operatorList.length; j++) {
          var op = country.operatorList[j]
          operatorMap[op.operatorId] = false
        }
        newSelectedMap[country.countryId] = {
          checked: false,
          indeterminate: false,
          operatorMap: operatorMap
        }
      }
      this.selectedCountryMap = newSelectedMap
    },

    initLetterMap: function () {
      this.letterCountryMap = {}
      for (var i = 0; i < this.fullCountryList.length; i++) {
        var country = this.fullCountryList[i]
        if (!country.firstLetter) {
          try {
            var firstPinyin = pinyin(country.countryName, { pattern: 'first', toneType: 'none' })[0]
            country.firstLetter = firstPinyin ? firstPinyin.toUpperCase() : '#'
          } catch (e) {
            country.firstLetter = '#'
          }
        }
        var letter = country.firstLetter
        if (!this.letterCountryMap[letter]) {
          this.letterCountryMap[letter] = []
        }
        this.letterCountryMap[letter].push(country.countryId)
      }
    },

    handleCountryChange: function (country) {
      var countryState = this.selectedCountryMap[country.countryId]
      if (!countryState) return
      var operatorIds = Object.keys(countryState.operatorMap)
      for (var i = 0; i < operatorIds.length; i++) {
        var operatorId = operatorIds[i]
        this.$set(countryState.operatorMap, operatorId, countryState.checked)
      }
      countryState.indeterminate = false
      this.$set(this.selectedCountryMap, country.countryId, JSON.parse(JSON.stringify(countryState)))
    },

    handleOperatorChange: function (country) {
      var countryState = this.selectedCountryMap[country.countryId]
      if (!countryState) return
      var operatorList = country.operatorList
      var checkedCount = 0
      var operatorCheckedList = Object.values(countryState.operatorMap)
      for (var i = 0; i < operatorCheckedList.length; i++) {
        if (operatorCheckedList[i]) {
          checkedCount++
        }
      }
      var totalCount = operatorList.length

      if (checkedCount === totalCount) {
        countryState.checked = true
        countryState.indeterminate = false
      } else if (checkedCount === 0) {
        countryState.checked = false
        countryState.indeterminate = false
      } else {
        countryState.checked = false
        countryState.indeterminate = true
      }
      this.$set(this.selectedCountryMap, country.countryId, JSON.parse(JSON.stringify(countryState)))
    },

    scrollToLetter: function (letter) {
      if (!this.letterCountryMap[letter]) return
      var firstCountryId = this.letterCountryMap[letter][0]
      var targetEl = document.getElementById('country-' + firstCountryId)
      if (targetEl && this.$refs.countryListWrapper) {
        targetEl.scrollIntoView({ behavior: 'smooth', block: 'start' })
        this.activeLetter = letter
      }
    },

    handleScroll: function () {
      var wrapper = this.$refs.countryListWrapper
      if (!wrapper) return
      var countryItems = wrapper.querySelectorAll('.country-item')
      for (var i = 0; i < countryItems.length; i++) {
        var item = countryItems[i]
        var rect = item.getBoundingClientRect()
        var wrapperRect = wrapper.getBoundingClientRect()
        if (rect.top <= wrapperRect.top + 10) {
          var countryId = item.id.replace('country-', '')
          var country = this.fullCountryList.find(function (c) {
            return c.countryId == countryId
          })
          if (country && country.firstLetter) {
            this.activeLetter = country.firstLetter
          }
        }
      }
    },

    handleDialogClose: function () {
      this.dialogVisible = false
      this.resetSelection()
    },

    confirmSelection: function () {
      var selectedResult = []
      for (var i = 0; i < this.fullCountryList.length; i++) {
        var country = this.fullCountryList[i]
        var state = this.selectedCountryMap[country.countryId]
        if (state && (state.checked || state.indeterminate)) {
          var selectedOperators = []
          for (var j = 0; j < country.operatorList.length; j++) {
            var op = country.operatorList[j]
            if (state.operatorMap[op.operatorId]) {
              selectedOperators.push(op)
            }
          }
          selectedResult.push({
            countryId: country.countryId,
            countryName: country.countryName,
            countryCode: country.countryCode,
            selectedOperators: selectedOperators
          })
        }
      }

      this.$emit("select-complete", selectedResult)
      this.dialogVisible = false
      this.resetSelection()
    },

    openDialog: function (defaultSelected) {
      var _this = this
      if (!defaultSelected) defaultSelected = []
      this.initSelectedMap()
      this.initLetterMap()
      this.dialogVisible = true
      
      this.$nextTick(function () {
        if (defaultSelected.length > 0) {
          for (var i = 0; i < defaultSelected.length; i++) {
            var selectedCountry = defaultSelected[i]
            var countryState = _this.selectedCountryMap[selectedCountry.countryId]
            if (!countryState) continue
            for (var j = 0; j < selectedCountry.selectedOperators.length; j++) {
              var op = selectedCountry.selectedOperators[j]
              if (countryState.operatorMap[op.operatorId] !== undefined) {
                _this.$set(countryState.operatorMap, op.operatorId, true)
              }
            }
            var targetCountry = _this.fullCountryList.find(function (c) {
              return c.countryId === selectedCountry.countryId
            })
            if (targetCountry) {
              _this.handleOperatorChange(targetCountry)
            }
          }
        }
        if (_this.$refs.countryListWrapper) {
          _this.$refs.countryListWrapper.addEventListener('scroll', _this.handleScroll)
          _this.$refs.countryListWrapper.scrollTop = 0
        }
      })
    },

    resetSelection: function () {
      this.searchKeyword = ''
      this.activeLetter = ''
      this.initSelectedMap()
      if (this.$refs.countryListWrapper) {
        this.$refs.countryListWrapper.scrollTop = 0
      }
    }
  },
  beforeDestroy: function () {
    if (this.searchTimer) clearTimeout(this.searchTimer)
    if (this.$refs.countryListWrapper) {
      this.$refs.countryListWrapper.removeEventListener('scroll', this.handleScroll)
    }
  }
}
</script>

<style lang="less" scoped>
// 弹框全局样式
/deep/ .country-operator-dialog {
  .el-dialog__body {
    padding: 12px 20px;
  }
}

// 顶部操作区
.header-operation {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;

  .all-select-checkbox {
    flex-shrink: 0;
    font-size: 14px;
  }

  .search-input {
    flex: 1;
  }
}

// 内容区：滚动列表 + 字母导航
.content-wrapper {
  display: flex;
  gap: 12px;
  height: 420px;
  position: relative;
}

// 国家列表滚动容器
.country-list-wrapper {
  flex: 1;
  height: 100%;
  overflow-y: auto;
  padding-right: 8px;

  .country-item {
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    margin-bottom: 8px;
    overflow: hidden;
    background: #fff;

    // ==================== 核心修复：国家头部垂直居中 ====================
    .country-header {
      padding: 0 14px;
      height: 44px;
      /* 移除line-height，避免和flex布局冲突 */
      display: flex;
      align-items: center;
      border-bottom: 1px solid #f0f0f0;

      .country-checkbox {
        width: 100%;
        display: flex;
        align-items: center;
        font-size: 15px;

        // 穿透修改ElementUI复选框内部样式，确保垂直居中
        /deep/ .el-checkbox {
          display: flex;
          align-items: center;
          margin-right: 0;
          width: 100%;

          .el-checkbox__input {
            line-height: 1;
          }

          .el-checkbox__label {
            display: flex;
            align-items: center;
            padding-left: 8px;
            line-height: 1;
          }
        }

        .country-name {
          font-weight: 500;
          color: #303133;
          margin-right: 4px;
        }

        .country-code {
          color: #909399;
          font-size: 13px;
        }
      }
    }
    // ================================================================

    // 运营商列表
    .operator-list {
      padding: 12px 14px 12px 38px;
      display: flex;
      flex-wrap: wrap;
      gap: 12px 16px;

      .operator-checkbox {
        font-size: 14px;
        padding: 6px 12px;
        border: 1px solid #dcdfe6;
        border-radius: 4px;
        transition: all 0.2s;
        min-width: 100px;

        &.is-checked {
          border-color: #409eff;
          background: #ecf5ff;
        }

        &:hover {
          border-color: #409eff;
        }
      }
    }
  }

  // 空数据提示
  .empty-tip {
    text-align: center;
    color: #909399;
    padding: 40px 0;
    font-size: 14px;
  }
}

// 字母导航栏
.letter-nav {
  width: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: 4px 0;
  background: #f5f7fa;
  border-radius: 12px;
  flex-shrink: 0;

  .letter-item {
    width: 20px;
    height: 16px;
    line-height: 16px;
    text-align: center;
    font-size: 12px;
    color: #606266;
    border-radius: 3px;
    cursor: pointer;
    user-select: none;
    transition: all 0.2s;

    &:hover:not(.disabled) {
      background: #409eff;
      color: #fff;
    }

    &.active {
      background: #409eff;
      color: #fff;
    }

    &.disabled {
      color: #c0c4cc;
      cursor: not-allowed;
    }
  }
}

// 选中预览
.selected-preview {
  margin-top: 12px;
  padding: 8px 12px;
  background: #ecf5ff;
  border-radius: 4px;
  font-size: 13px;
  color: #409eff;

  .selected-count {
    font-weight: 600;
  }
}

// 滚动条样式优化
.country-list-wrapper::-webkit-scrollbar {
  width: 6px;
}
.country-list-wrapper::-webkit-scrollbar-thumb {
  background: #c0c4cc;
  border-radius: 3px;
}
.country-list-wrapper::-webkit-scrollbar-track {
  background: #f5f7fa;
}
</style>